import Image from 'next/image'
import Link from 'next/link'

import { ArticleDto } from '@/domains/articles/application/dto/getArticles.dto'

/**
 * Une entrée du journal, en ligne plutôt qu'en carte : le titre est
 * l'information, la vignette l'accompagne. Composant serveur — la
 * version précédente était cliente uniquement pour un fondu au scroll.
 */
export default function ArticleCard({
  title,
  desc,
  date,
  readTime,
  category,
  image,
  slug,
}: ArticleDto) {
  return (
    <article className="group border-b border-ink/15">
      <Link
        href={`/articles/${slug}`}
        className="grid gap-5 py-8 tablet:grid-cols-12 tablet:gap-8"
      >
        <div className="tablet:col-span-3">
          <div className="relative aspect-4/3 overflow-hidden rounded-[3px] bg-trace">
            <Image
              src={image}
              alt=""
              fill
              sizes="(max-width: 479px) 100vw, 25vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>
        </div>

        <div className="tablet:col-span-8 tablet:col-start-5">
          {/* EZIO peut renvoyer une catégorie nulle et des champs vides :
              on n'affiche que les métadonnées réellement présentes,
              séparateurs compris. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-graphite">
            {[
              category ? (
                <span key="cat" className="text-signal-deep">
                  {category}
                </span>
              ) : null,
              date ? <span key="date">{date}</span> : null,
              readTime ? <span key="read">{readTime}</span> : null,
            ]
              .filter(Boolean)
              .flatMap((node, i) =>
                i === 0
                  ? [node]
                  : [
                      <span key={`sep-${i}`} aria-hidden="true">
                        /
                      </span>,
                      node,
                    ]
              )}
          </div>

          <h3 className="mt-4 font-display text-2xl tablet:text-3xl font-bold leading-tight tracking-tight text-ink">
            <span className="link-draw">{title}</span>
          </h3>

          <p className="mt-4 max-w-[62ch] text-[0.9375rem] leading-relaxed text-anthracite">
            {desc}
          </p>

          <span className="mt-5 inline-flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-ink">
            Lire l&apos;article
            <span
              aria-hidden="true"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </span>
        </div>
      </Link>
    </article>
  )
}
